import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button, Modal, Row, Col, Form, OverlayTrigger } from "react-bootstrap";
import { MdFilterList, MdAddCircle, MdEdit, MdDelete } from "react-icons/md";
import { ToastContainer, toast } from "react-toastify";
import { Tooltip } from "@mui/material";
import loading from "../../../assets/images/common/loading.gif";
import DataTable from "react-data-table-component";

function ServiceMaster() {
  const [showFilterModal, setShowFilterModal] = useState(false);
  const handleCloseFilterModal = () => setShowFilterModal(false);
  const handleShowFilterModal = () => setShowFilterModal(true);

  const [serviceData, setServiceData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  const readOnlyRoles = ["Class Teacher", "Teacher", "Class Incharge"];
  const canSubmit = !readOnlyRoles.includes(userObj.role_name?.trim());

  const fetchServiceData = async () => {
    try {
      const response = await axios.post(baseUrl + "/service_master/search/", {
        service_account_master_id: 0,
        school_id: userObj.school_id,
      });
      const rows = response.data || [];
      const mapped = rows.map((r) => ({
        ...r,
        service_id: r.service_master_id,
        account_code: r.acc_code,
        account_master_id: r.service_account_master_id,
        // default to true when backend doesn’t send is_active
        is_active:
          typeof r.is_active === "boolean"
            ? r.is_active
            : r.is_active === "false"
            ? false
            : true,
      }));
      setServiceData(mapped);
    } catch (error) {
      console.error("Error fetching Service data:", error);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchServiceData().finally(() => setIsLoading(false));
  }, []);

  // 🔹 EDIT CLICK – now takes full row instead of just ID
  const handleEditClick = async (row) => {
    // get the ID from row
    const service_id = row.service_id || row.service_master_id;

    if (!service_id) {
      console.error("Service ID missing for edit.");
      navigate("/finance/services/add");
      return;
    }

    // build base object for AddServiceMaster
    let serviceDataForEdit = {
      ...row,
      service_master_id: service_id,
      account_master_id: row.account_master_id,
      account_code: row.account_code,
      effective_date: null,
      is_tds_mandatory: "N",
      lines: [],
    };

    try {
      const resp = await axios.post(
        baseUrl + "/service_master_detail/by_service/",
        {
          service_master_id: service_id,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      const details = Array.isArray(resp.data) ? resp.data : [];
      if (details.length > 0) {
        const firstDetail = details[0];
        serviceDataForEdit = {
          ...serviceDataForEdit,
          effective_date: firstDetail.effective_date || null,
          is_tds_mandatory: firstDetail.is_tds ? "Y" : "N",
          lines: details,
        };
      }

      // navigate to Add page with ServiceData
      navigate("/finance/services/add", {
        state: { ServiceData: serviceDataForEdit },
      });
    } catch (error) {
      console.error("Error fetching Service details for edit:", error);
      toast.error("Failed to load service details for edit");

      // still navigate with whatever we have so user can edit header at least
      navigate("/finance/services/add", {
        state: { ServiceData: serviceDataForEdit },
      });
    }
  };

  // Uses inactive_service_master to toggle status
  const handleDeleteClick = async (service_id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to change the status?"
    );
    if (!confirmDelete) return;

    try {
      const modifiedBy =
        userObj.email_id || userObj.user_name || userObj.username || "system";

      const response = await axios.post(
        baseUrl + "/service_master/inactive/",
        {
          service_master_id: service_id,
          modified_by: modifiedBy,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const { message, is_active } = response.data || {};

      setServiceData((prev) =>
        prev.map((row) =>
          row.service_id === service_id ? { ...row, is_active } : row
        )
      );

      toast.success(message || "Record set to InActive");
    } catch (error) {
      console.error("Error updating Service status:", error);
      toast.error("Failed to change status");
    }
  };

  const baseColumns = useMemo(
    () => [
      {
        name: "Account Code",
        selector: (row) => row.account_code,
        cell: (row) => (
          <Tooltip title={row.account_code || ""}>
            <span>{row.account_code}</span>
          </Tooltip>
        ),
        sortable: true,
      },
      {
        name: "Status",
        selector: (row) => row.is_active,
        cell: (row) => {
          // 🔧 SPECIAL CASE: No Records row
          if (row.service_id === "No Records Found") {
            return (
              <span style={{ color: "red", fontWeight: 600 }}>
                No Records Found
              </span>
            );
          }

          const statusLabel =
            row.is_active === false || row.is_active === "false"
              ? "InActive"
              : "Active";
          return (
            <Tooltip title={statusLabel}>
              <span>{statusLabel}</span>
            </Tooltip>
          );
        },
        sortable: true,
      },
    ],
    []
  );

  const columns = useMemo(
    () =>
      canSubmit
        ? [
            ...baseColumns,
            {
              name: "Actions",
              cell: (row) => {
                // 🔧 Do NOT show actions for "No Records" row
                if (!row.service_id || row.service_id === "No Records Found") {
                  return null;
                }

                return (
                  <div className="tableActions">
                    <Tooltip title="Edit" arrow>
                      <span
                        className="commonActionIcons"
                        // Pass the full row into handleEditClick
                        onClick={() => handleEditClick(row)}
                      >
                        <MdEdit />
                      </span>
                    </Tooltip>
                    <Tooltip title="Change Status" arrow>
                      <span
                        className="commonActionIcons"
                        onClick={() => handleDeleteClick(row.service_id)}
                      >
                        <MdDelete />
                      </span>
                    </Tooltip>
                  </div>
                );
              },
            },
          ]
        : baseColumns,
    [baseColumns, canSubmit]
  );

  const searchableColumns = [(r) => r.account_code];

  const filteredRecords = (serviceData || []).filter((row) =>
    searchableColumns.some((selector) =>
      String(selector(row) || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    )
  );

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const [form, setForm] = useState({
    account_code: "",
    is_active: "",
    action: "FILTER",
  });

  const handleFilterSubmit = async (e) => {
  e.preventDefault();

  const createdBy =
    userObj.email_id || userObj.user_name || userObj.username || "system";

  // 🧹 Clean & validate account_code before sending
  const rawAccountCode = form.account_code || "";
  const cleanedAccountCode = rawAccountCode.replace(/\s+/g, ""); // remove spaces

  let accountCodeInt = 0; // default for "no specific filter"

  if (cleanedAccountCode !== "") {
    // ensure it's only digits
    if (!/^\d+$/.test(cleanedAccountCode)) {
      toast.error("Account Code should contain digits only");
      return;
    }
    accountCodeInt = parseInt(cleanedAccountCode, 10);
  }

  // valid dummy datetime for FILTER mode (Pydantic needs a datetime)
  const dummyEffectiveDate = new Date().toISOString();

  const payload = {
    // root-level fields required by your Pydantic model
    service_master_id: 0,
    created_by: createdBy,
    action: "FILTER",
    school_id: userObj.school_id,

    header: {
      account_code: accountCodeInt, // 🔹 send integer, not string
      is_active: form.is_active,
      action: "FILTER",
      school_id: userObj.school_id,
      effective_date: dummyEffectiveDate,
      is_tds_mandatory: "N",
      tds_account_master_id: 0,
    },
    lines: [],
  };

  try {
    const resp = await axios.post(baseUrl + "/servicemaster/", payload, {
      headers: { "Content-Type": "application/json" },
    });

    let rows = [];
    if (Array.isArray(resp.data)) {
      rows = resp.data;
    } else if (Array.isArray(resp.data?.data)) {
      rows = resp.data.data;
    } else {
      setServiceData([]);
      setShowFilterModal(false);
      return;
    }

    const mapped = rows.map((r) => ({
      ...r,
      service_id: r.service_master_id ?? r.service_id,
      account_code: r.account_code ?? r.acc_code,
      account_master_id: r.service_account_master_id ?? r.account_master_id,
      is_active:
        typeof r.is_active === "boolean"
          ? r.is_active
          : r.is_active === "false"
          ? false
          : true,
    }));

    setServiceData(mapped);
    setShowFilterModal(false);
  } catch (error) {
    console.error("Filter error:", error);
    toast.error("Failed to fetch filtered data");
  }
};


  const handleReset = async () => {
    setForm({
      account_code: "",
      is_active: "",
      action: "FILTER",
    });
    fetchServiceData();
  };

  const ExpandedComponent = ({ data }) => {
    const [details, setDetails] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(true);

    useEffect(() => {
      let isMounted = true;
      const fetchDetails = async () => {
        try {
          const resp = await axios.post(
            baseUrl + "/service_master_detail/by_service/",
            {
              service_master_id: data.service_id,
            },
            {
              headers: { "Content-Type": "application/json" },
            }
          );
          if (!isMounted) return;
          setDetails(resp.data || []);
        } catch (err) {
          console.error("Error fetching service details:", err);
          toast.error("Failed to load service details");
        } finally {
          if (isMounted) setLoadingDetails(false);
        }
      };
      fetchDetails();
      return () => {
        isMounted = false;
      };
    }, [data.service_id]);

    if (loadingDetails) {
      return (
        <div
          style={{
            padding: "12px",
            marginLeft: "80px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            background:
              "linear-gradient(135deg, rgb(249, 249, 249) 0%, rgb(230, 230, 230) 100%)",
            boxShadow: "rgba(0, 0, 0, 0.1) 2px 4px 10px",
          }}
        >
          <img src={loading} alt="Loading..." className="loadingGif" />
        </div>
      );
    }

    return (
      <div
        style={{
          padding: "12px",
          marginLeft: "80px",
          border: "1px solid #ccc",
          borderRadius: "8px",
          background:
            "linear-gradient(135deg, rgb(249, 249, 249) 0%, rgb(230, 230, 230) 100%)",
          boxShadow: "rgba(0, 0, 0, 0.1) 2px 4px 10px",
        }}
      >
        <table className="table">
          <thead>
            <tr>
              <th>Effective Date</th>
              <th>Is TDS</th>
              <th>TDS Code</th>
              <th>Code Type</th>
              <th>Code</th>
            </tr>
          </thead>
          <tbody>
            {(details || []).map((d) => (
              <tr key={d.service_master_detail_id}>
                <td>
                  {d.effective_date
                    ? new Date(d.effective_date).toLocaleDateString("en-GB")
                    : ""}
                </td>
                <td>{String(d.is_tds)}</td>
                <td>{d.tdsacc_code || ""}</td>
                <td>{d.code_type_name || ""}</td>
                <td>{d.hsn_sac_code || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="pageBody">
      <ToastContainer />
      <div className="commonDataTableHead">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <h6 className="commonTableTitle">Service Master</h6>
          </div>

          <div>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              className="searchInput"
              onChange={handleSearchChange}
            />
          </div>

          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}
            >
              <Button
                className="secondaryBtn"
                variant="secondary"
                onClick={handleShowFilterModal}
              >
                <MdFilterList />
              </Button>
            </OverlayTrigger>
            {canSubmit && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-top">Add</Tooltip>}
              >
                <Button
                  className="primaryBtn"
                  variant="primary"
                  onClick={() => navigate("/finance/services/add")}
                >
                  <MdAddCircle />
                </Button>
              </OverlayTrigger>
            )}
          </div>
        </div>
      </div>

      <div className="commonTable height100">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (
            <DataTable
              className="custom-table"
              columns={columns}
              data={
                Array.isArray(filteredRecords) && filteredRecords.length > 0
                  ? filteredRecords
                  : [
                      // 🔧 Dummy row when no records
                      {
                        service_id: "No Records Found",
                        account_code: "",
                        is_active: "",
                      },
                    ]
              }
              // pagination / expanders only when actual data exists
              pagination={
                Array.isArray(filteredRecords) && filteredRecords.length > 0
              }
              expandableRows={
                Array.isArray(filteredRecords) && filteredRecords.length > 0
              }
              expandableRowsComponent={ExpandedComponent}
              highlightOnHover
              responsive
              fixedHeader
              fixedHeaderScrollHeight="calc(100vh - 170px)"
            />
          )}
        </div>
      </div>

      <Modal
        show={showFilterModal}
        onHide={handleCloseFilterModal}
        className="commonFilterModal"
      >
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Filter</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modalBodyScrollable">
          <Form id="filterForm" onSubmit={handleFilterSubmit}>
            <Row>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group>
                    <Form.Label>Account Code</Form.Label>
                    <Form.Control
                      type="text"
                      value={form.account_code}
                      onChange={(e) =>
                        setForm({ ...form, account_code: e.target.value })
                      }
                    />
                  </Form.Group>
                </div>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="modalFooterFixed">
          <Button
            variant="secondary"
            className="btn-info clearBtn me-2"
            onClick={handleReset}
          >
            Reset
          </Button>
          <div>
            <Button
              variant="secondary"
              className="btn-danger secondaryBtn me-2"
              onClick={handleCloseFilterModal}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="btn-success primaryBtn"
              type="submit"
              form="filterForm"
            >
              Search
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ServiceMaster;
